import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type DateRangePreset = 'week' | 'month' | 'quarter' | 'year' | 'all' | 'custom';

interface DateRangeFilterProps {
  value?: DateRangePreset;
  onChange: (preset: DateRangePreset, dateRange?: { start: string; end: string }) => void;
  customDateRange?: { start: string; end: string };
  onCustomDateRangeChange?: (range: { start: string; end: string } | undefined) => void;
}

/**
 * Date range filter component
 *
 * Provides a dropdown selector with preset date ranges and a custom option
 * that shows start/end date inputs. Only emits a valid range to the parent
 * when both start and end dates are populated.
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value = 'all',
  onChange,
  customDateRange,
  onCustomDateRangeChange,
}) => {
  const { t } = useTranslation();

  // Local state for custom date inputs (tracks partial values independently)
  const [customStart, setCustomStart] = useState(customDateRange?.start ?? '');
  const [customEnd, setCustomEnd] = useState(customDateRange?.end ?? '');

  // Sync local state when parent provides new values
  useEffect(() => {
    if (customDateRange) {
      setCustomStart(customDateRange.start);
      setCustomEnd(customDateRange.end);
    }
  }, [customDateRange]);

  const calculateDateRange = (
    preset: DateRangePreset
  ): { start: string; end: string } | undefined => {
    if (preset === 'all' || preset === 'custom') {
      return undefined;
    }

    const now = new Date();
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    let start: Date;

    switch (preset) {
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case 'quarter':
        start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case 'year':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        return undefined;
    }

    return {
      start: formatDate(start),
      end: formatDate(now),
    };
  };

  const handleChange = (event: SelectChangeEvent<DateRangePreset>) => {
    const preset = event.target.value as DateRangePreset;
    const dateRange = calculateDateRange(preset);
    onChange(preset, dateRange);
  };

  const emitCustomRange = (start: string, end: string) => {
    // Only emit a valid date range when both fields are populated
    if (start && end) {
      const range = { start, end };
      onCustomDateRangeChange?.(range);
      onChange('custom', range);
    } else {
      onCustomDateRangeChange?.(undefined);
      onChange('custom', undefined);
    }
  };

  const handleCustomStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = event.target.value;
    setCustomStart(newStart);
    emitCustomRange(newStart, customEnd);
  };

  const handleCustomEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = event.target.value;
    setCustomEnd(newEnd);
    emitCustomRange(customStart, newEnd);
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="date-range-filter-label">{t('insight.filters.dateRange')}</InputLabel>
        <Select
          labelId="date-range-filter-label"
          value={value}
          onChange={handleChange}
          label={t('insight.filters.dateRange')}
        >
          <MenuItem value="week">Last Week</MenuItem>
          <MenuItem value="month">Last Month</MenuItem>
          <MenuItem value="quarter">Last Quarter (3 months)</MenuItem>
          <MenuItem value="year">Last Year</MenuItem>
          <MenuItem value="all">All Time</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>
      </FormControl>

      {value === 'custom' && (
        <>
          <TextField
            type="date"
            size="small"
            label="Start"
            value={customStart}
            onChange={handleCustomStartChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            type="date"
            size="small"
            label="End"
            value={customEnd}
            onChange={handleCustomEndChange}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
        </>
      )}
    </Box>
  );
};
